import { useEffect, useRef } from 'react';
import { useWebSocket } from '@utils/hooks/useWebSocket';
import { useDetailedOrders, useGetOrdersWithStatus } from '../../pages/Orders/hooks/orders';
import { OrderDetail, OrderEvent } from '@interfaces/OrderTypes';
import toast from 'react-hot-toast';
import { useNotificationContext } from '@context/NotificationsContext';
import { DELIVERY_MAP, NOTIFICATION_NAMES_MAP, PRINT_AUTO_OPTIONS_MAP, SOCKET_EVENTS } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import { useElectronPrintSettingsFromStore } from '@store/actions/electronPrintSettings';
import { useElectron } from './useElectron';
import useModules from './useModules';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useNavigate } from 'react-router-dom';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { useAuth } from '@context/AuthContext';
import { shouldIgnoreIncomingOrder, shouldAutoPrintIncomingOrder } from '@utils/hooks/incomingOrderVisibility';
import { isEventForCurrentCommerce } from '@utils/hooks/eventCommerceScope';
import { useWebBlePrint } from './useWebBlePrint';
import { getWebBlePrintMode } from '@utils/print/webble/printMode';
import { appStore } from '@store/index';
import { useMultisucursalGate } from '@utils/hooks/useMultisucursalGate';
export const useIncomingOrder = () => {
  const { subscribeEvent, unsubscribeEvent } = useWebSocket();
  const { getOrder } = useDetailedOrders();
  const { addNotification, removeNotification } = useNotificationContext();
  const { handleAddOrder, handleUpdateStatus } = useGetOrdersWithStatus();
  const allPrintersSettings = useElectronPrintSettingsFromStore();
  const { hasModule } = useModules();
  const { planId, accountCommerceTokens } = useSubscription();
  const accountCommerceTokensRef = useRef(accountCommerceTokens);
  const { printOrder } = useElectron();
  const { printOrder: webBlePrintOrder } = useWebBlePrint();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const { refreshLogin } = useAuth();
  // Multisucursal: un usuario sin permiso NO debe ver/oír ni imprimir pedidos de OTRAS sucursales.
  // En multi-sucursal todos los pedidos —incluido el propio— llegan por el canal de cuenta
  // `panel_<accountId>`, así que el filtro correcto es por notificación, no por suscripción (gatear
  // la suscripción rompió el tiempo real → incidente #1004). El gate (root, o no-root con el permiso
  // PEDIDOS_MULTISUCURSAL) está centralizado en useMultisucursalGate y lo comparten el dropdown
  // (useCanSwitchBranch) y el session-guard (useBranchSessionGuard).
  const canSeeOtherBranchOrders = useMultisucursalGate();
  // Vía ref (igual que accountCommerceTokens) para que el callback del socket lea SIEMPRE el valor
  // más reciente y no quede atrapado en un closure obsoleto cuando los módulos/permiso cargan o
  // cambian sin remount (relevante al montar useSubscriptionSocket con refetch en vivo).
  const canSeeOtherBranchOrdersRef = useRef(canSeeOtherBranchOrders);

  // Keep the ref in sync so the socket callback always has the latest tokens
  useEffect(() => {
    accountCommerceTokensRef.current = accountCommerceTokens;
  }, [accountCommerceTokens]);

  useEffect(() => {
    canSeeOtherBranchOrdersRef.current = canSeeOtherBranchOrders;
  }, [canSeeOtherBranchOrders]);

  useEffect(() => {
    // Auto-print por impresora Web Bluetooth (BLE), espejo de la rama Electron. Aislado y
    // a prueba de fallos: NUNCA debe romper la recepción de pedidos. Lee estado FRESCO del
    // store y del master toggle. `webBlePrintOrder` maneja las copias internamente.
    const autoPrintWebBle = (order: OrderDetail, trigger: 'received' | 'accepted') => {
      try {
        if (!getWebBlePrintMode()) return;
        // FIX (HIGH, stale closure): leer los módulos FRESCOS del store en el momento del
        // evento de socket, NO el `hasModule` capturado en el closure del useEffect. Las deps
        // del effect están incompletas a propósito (agregar `hasModule`/módulos lo re-suscribe
        // y puede reintroducir el incidente #1004), así que el closure puede quedar STALE cuando
        // los módulos cargan tarde → el auto-print BLE se saltaba para siempre. Replicamos la
        // misma lógica de `useModules.hasModule` (some(m.id === ...)) leyendo el slice fresco,
        // igual que ya hacemos con `getWebBlePrintMode()` y `webBlePrinters`.
        const modules = appStore.getState().subscription?.modulesAndPermissions || [];
        const hasAutoPrintModule = modules.some((m) => m.id === 'IMPRESION_AUTOMATICA');
        if (!hasAutoPrintModule) return;
        if (order.pedido.estatus === 'pre_checkout') return;
        const printers = appStore.getState().webBlePrinters || {};
        // FIX (HIGH, teléfono 'accepted' vs 'received'): un pedido telefónico nace ACEPTADO y
        // dispara una sola vez el evento de socket con trigger 'accepted' (no hay un evento
        // 'received' previo separado como en los pedidos normales). Paridad con Electron, que
        // para el pedido telefónico imprime AMBOS sets: las impresoras "Al aceptar"
        // (onOrderAccepted) y las "Al recibir" (onOrderReceived). Por eso, en el trigger
        // 'accepted', debemos imprimir tanto las impresoras 'confirmed' (Al aceptar) como las
        // 'all' (Al recibir). En el trigger 'received' (pedido normal recién entrado) solo
        // imprimimos las 'all'; sus impresoras 'confirmed' se imprimen luego al aceptar
        // manualmente (otra llamada con 'accepted'). Así un pedido normal NO se imprime doble:
        // 'received' nunca toca las 'confirmed', y 'accepted' solo lo dispara una transición.
        const wanted: Array<'all' | 'confirmed'> = trigger === 'accepted' ? ['confirmed', 'all'] : ['all'];
        Object.values(printers).forEach((p) => {
          if (p.enabled && (p.deviceId || p.deviceName) && wanted.includes(p.autoPrint as 'all' | 'confirmed')) {
            webBlePrintOrder({ detailedOrder: order, orderId: order.pedido.id, settings: p });
          }
        });
      } catch {
        /* el auto-print BLE nunca debe romper la recepción de pedidos */
      }
    };

    const onNewOrder = async (incomingOrder: OrderEvent) => {
      try {
        const commerceToken = accountCommerceTokensRef.current?.find(
          (item) => item.id_comercio === incomingOrder.commerceId,
        );
        const order = await getOrder(incomingOrder.orderId);
        // No agregar pedidos programados que aún no se activan al Kanban
        if (order.pedido.estatus === 'scheduled') return;
        if (order.pedido.is_scheduled && order.pedido.auto_fire_at && new Date(order.pedido.auto_fire_at) > new Date())
          return;
        const isCurrentCommerce = isEventForCurrentCommerce(
          incomingOrder.commerceId,
          localStorage.getItem('id_commerce'),
        );
        // Ignorar por completo (sin Kanban, sin toast, sin alarma, sin impresión) los pedidos de
        // otras sucursales cuando el usuario no tiene permiso multisucursal. La suscripción al
        // canal de cuenta se mantiene intacta (es la que entrega también los pedidos propios).
        if (shouldIgnoreIncomingOrder(isCurrentCommerce, canSeeOtherBranchOrdersRef.current)) return;
        if (isCurrentCommerce) handleAddOrder(order.pedido);
        if (order.pedido.origin === 'telefono') {
          toast.success(`Pedido ${order.pedido.folio} registrado`);
          trackEvent(TRACK_EVENT_NAMES.MenuOrderPhoneNewOrder);

          navigate('/control-pedidos');
          // Módulo LEÍDO FRESCO del store (no el `hasModule` del closure, que puede quedar stale si
          // los módulos cargan tarde → dejaría muerto el auto-print —Electron Y BLE— para siempre).
          if (
            shouldAutoPrintIncomingOrder(
              isCurrentCommerce,
              (appStore.getState().subscription?.modulesAndPermissions || []).some(
                (m) => m.id === 'IMPRESION_AUTOMATICA',
              ),
            )
          ) {
            Object.keys(allPrintersSettings).forEach((key) => {
              if (
                allPrintersSettings[key as keyof typeof allPrintersSettings].electronEnabled !== false &&
                allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrinterName &&
                allPrintersSettings[key as keyof typeof allPrintersSettings].electronAutoPrint ===
                  PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key
              ) {
                new Array(allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrintNumber)
                  .fill('mock')
                  .forEach((_, index) => {
                    setTimeout(
                      () => {
                        printOrder({
                          detailedOrder: order,
                          orderId: order.pedido.id,
                          settings: allPrintersSettings[key as keyof typeof allPrintersSettings],
                        });
                      },
                      allPrintersSettings[key as keyof typeof allPrintersSettings]
                        .electronMultiPrintDelayBetweenPrints *
                        index *
                        1000,
                    );
                  });
              }
            });
            autoPrintWebBle(order, 'accepted');
          }
        } else {
          const isSelfService = order?.pedido?.self_service?.order_uuid;

          addNotification({
            id: order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key,
            title: 'Pedido nuevo',
            subtitle: `No: ${order.pedido.folio}`,
            description: isSelfService
              ? `Mesa: ${order.pedido.self_service?.table_number}`
              : `Cliente: ${order.pedido.nombre}`,
            body: (
              <div className="flex">
                <p className="mr-3">
                  {order.productos.length} producto{order.productos.length !== 1 ? 's' : ''}
                </p>
                <p className="font-semibold text-color-secondary">Total: {formatToCurrency(order.pedido.total)}</p>
              </div>
            ),
            icon: DELIVERY_MAP[order.pedido.metodo_entrega].icon,
            accentColor: DELIVERY_MAP[order.pedido.metodo_entrega].iconColor,
            metodo_entrega: order.pedido.metodo_entrega,
            acceptLabel: isCurrentCommerce ? undefined : 'Ver',
            onAccept: isCurrentCommerce
              ? (cook_time?: number) => {
                  handleUpdateStatus(order.pedido, 'next', true, cook_time);
                  removeNotification(order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key, true);

                  if (hasModule('IMPRESION_AUTOMATICA')) {
                    Object.keys(allPrintersSettings).forEach((key) => {
                      if (
                        allPrintersSettings[key as keyof typeof allPrintersSettings].electronEnabled !== false &&
                        allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrinterName &&
                        allPrintersSettings[key as keyof typeof allPrintersSettings].electronAutoPrint ===
                          PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key
                      ) {
                        new Array(allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrintNumber)
                          .fill('mock')
                          .forEach((_, index) => {
                            setTimeout(
                              () => {
                                printOrder({
                                  detailedOrder: order,
                                  orderId: order.pedido.id,
                                  settings: allPrintersSettings[key as keyof typeof allPrintersSettings],
                                });
                              },
                              allPrintersSettings[key as keyof typeof allPrintersSettings]
                                .electronMultiPrintDelayBetweenPrints *
                                index *
                                1000,
                            );
                          });
                      }
                    });
                  }
                  autoPrintWebBle(order, 'accepted');
                }
              : () => refreshLogin(commerceToken?.token || '', commerceToken?.id_comercio || ''),
            rejectLabel: 'Ignorar',
            // "Ignorar" debe estar SIEMPRE disponible, también para pedidos de OTRA sucursal.
            // Antes era `undefined` para los ajenos → Notification.tsx solo renderiza el botón con
            // `{onReject && ...}`, así que no aparecía y la alarma en loop no podía silenciarse sin
            // cambiar de sucursal y volver. removeNotification(id, true) detiene el sonido solo si no
            // quedan otras alarmas activas (ver NotificationsContext: silenciado colateral).
            onReject: () => removeNotification(order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key, true),
          });
        }
        // Módulo LEÍDO FRESCO del store (no el `hasModule` del closure, que puede quedar stale si
        // los módulos cargan tarde → dejaría muerto el auto-print —Electron Y BLE— para siempre).
        if (
          shouldAutoPrintIncomingOrder(
            isCurrentCommerce,
            (appStore.getState().subscription?.modulesAndPermissions || []).some(
              (m) => m.id === 'IMPRESION_AUTOMATICA',
            ),
          )
        ) {
          Object.keys(allPrintersSettings).forEach((key) => {
            if (
              allPrintersSettings[key as keyof typeof allPrintersSettings].electronEnabled !== false &&
              allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrinterName &&
              allPrintersSettings[key as keyof typeof allPrintersSettings].electronAutoPrint ===
                PRINT_AUTO_OPTIONS_MAP.onOrderReceived.key
            ) {
              new Array(allPrintersSettings[key as keyof typeof allPrintersSettings].electronPrintNumber)
                .fill('mock')
                .forEach((_, index) => {
                  setTimeout(
                    () => {
                      // No imprimir automáticamente órdenes en pre_checkout
                      if (order.pedido.estatus !== 'pre_checkout') {
                        printOrder({
                          detailedOrder: order,
                          orderId: order.pedido.id,
                          settings: allPrintersSettings[key as keyof typeof allPrintersSettings],
                        });
                      }
                    },
                    allPrintersSettings[key as keyof typeof allPrintersSettings].electronMultiPrintDelayBetweenPrints *
                      index *
                      1000,
                  );
                });
            }
          });
          // Solo para pedidos NO telefónicos: el pedido telefónico ya disparó arriba
          // `autoPrintWebBle(order, 'accepted')`, que para teléfono imprime AMBOS sets
          // ('confirmed' + 'all'). Volver a llamarlo aquí con 'received' imprimiría las
          // impresoras 'all' por segunda vez (doble impresión). El bloque Electron de arriba
          // (líneas onOrderReceived) sigue intacto para no tocar su lógica.
          if (order.pedido.origin !== 'telefono') autoPrintWebBle(order, 'received');
        }
      } catch (e) {
        toast.error('Error al obtener un pedido entrante');
      }
    };

    subscribeEvent(SOCKET_EVENTS.NEW_ORDER, onNewOrder);

    return () => {
      unsubscribeEvent(SOCKET_EVENTS.NEW_ORDER, onNewOrder);
    };
  }, [handleAddOrder, allPrintersSettings, planId]);
};
